import {useChildRouteQuery} from '@github-ui/react-core/future/use-route-query'
import type {ReposFilterProps} from '@github-ui/repos-filter'
import type {PropertyDefinition} from '@github-ui/repos-filter/providers'
import type {SidebarLink} from '@github-ui/saved-views'
import {getShortcutsWithSaved, useShortcutDeletion, useShortcutEditor} from '@github-ui/saved-views'
import {NoteIcon} from '@primer/octicons-react'
import {Link, Tooltip} from '@primer/react'
import {useEffect, useEffectEvent, useState} from 'react'

import {getItemById, getItemFromQuery} from '../components/FinderSidebar'
import {useReposFinderStateContext} from '../contexts/ReposFinderStateContext'
import {reposFinderPageRoute} from '../routes/repos-finder-page-route'
import type {PagePayload} from '../types'

const customPropertiesDocsUrl =
  'https://docs.github.com/en/organizations/managing-organization-settings/managing-custom-properties-for-repositories-in-your-organization#searching-and-filtering-repositories-by-custom-properties-values'

/**
 * Hook to manage the state of the repos finder layout, including query state,
 * repository shortcuts, and sidebar selection. Gets data from context rather than props.
 * Provides methods for updating and submitting queries, managing shortcuts, and selecting sidebar links.
 *
 * @returns An object containing:
 *   - shortcuts: Array of repository shortcuts
 *   - selectedSidebarItem: Currently selected sidebar link
 *   - editing: Shortcut editing state and methods
 *   - deleting: Shortcut deletion state and methods
 *   - methods: Object with selectSidebarLink, createShortcut, editShortcut, and fetchResults functions
 *   - reposFilterProps: Props for the repository filter component
 */
export function useReposFinderLayoutState() {
  const {payload, fetchedQuery, queryParam, fetchResults, locationSidebarItemId, hasLocationState} =
    useReposFinderStateContext()

  const [query, setQuery] = useState(queryParam)

  const [shortcuts, setShortcuts] = useState(() =>
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion -- assertion required for type narrowing; removing it breaks tsc
    payload.repoShortcuts.map(shortcut => ({...shortcut, searchType: 'repositories'}) as SidebarLink),
  )

  function deriveSidebarItem(q: string, sidebarItemId: string | undefined, fromState: boolean) {
    if (fromState) {
      return sidebarItemId ? getItemById(sidebarItemId, shortcuts) : undefined
    }
    return getItemFromQuery(q, shortcuts)
  }

  const [selectedSidebarItem, setSelectedSidebarItem] = useState<SidebarLink | undefined>(() =>
    deriveSidebarItem(query, locationSidebarItemId, hasLocationState),
  )

  const deriveSelectedSidebarItem = useEffectEvent(() =>
    deriveSidebarItem(queryParam, locationSidebarItemId, hasLocationState),
  )

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setQuery(queryParam)
    setSelectedSidebarItem(deriveSelectedSidebarItem())
    // Only revert query & sidebar item upon navigation
  }, [queryParam, locationSidebarItemId, hasLocationState])

  const onShortcutDeleted = (deletedShortcut: SidebarLink) => {
    setShortcuts(shortcuts.filter(shortcut => shortcut.id !== deletedShortcut.id))
    setSelectedSidebarItem(undefined)
  }
  const deleting = useShortcutDeletion(onShortcutDeleted)

  const onShortcutSaved = (savedShortcut: SidebarLink) => {
    setShortcuts(getShortcutsWithSaved(shortcuts, savedShortcut))

    if (fetchedQuery !== savedShortcut.query) {
      fetchResults({q: savedShortcut.query, page: 1, sidebarItemId: savedShortcut.id})
    } else {
      setSelectedSidebarItem(savedShortcut)
    }
  }
  const editing = useShortcutEditor({type: 'repositories', onShortcutSaved})

  const {data: pagePayload} = useChildRouteQuery(reposFinderPageRoute, 'mainQuery')
  const definitions = pagePayload?.customProperties?.definitions
  const additionalFilterProviders = pagePayload?.additionalFilterProviders

  const trailingVisual =
    hasCustomProps(pagePayload) || hasDeploymentProps(pagePayload) ? (
      <CustomPropertiesVisualIndicator payload={pagePayload} />
    ) : null

  function updateQuery(q: string) {
    setQuery(q)
    if (editing.shortcut) {
      editing.setValue('query', q)
    }
  }

  async function kindlyDismissEditing() {
    if (editing.shortcut) {
      const dismissed = await editing.dismiss()
      if (!dismissed) return false
    }
    return true
  }

  async function selectSidebarLink(linkItem: SidebarLink) {
    if (!(await kindlyDismissEditing())) return
    deleting.reset()

    fetchResults({
      q: linkItem.query,
      page: 1,
      sidebarItemId: linkItem.id,
      focusHeaderAction: true,
    })
  }

  async function createShortcut() {
    if (!(await kindlyDismissEditing())) return

    setSelectedSidebarItem(undefined)
    deleting.reset()
    editing.create(query, selectedSidebarItem)
  }

  function editShortcut(shortcut: SidebarLink) {
    deleting.reset()
    editing.edit(shortcut, query)
  }

  function updateShortcut(shortcut: SidebarLink) {
    deleting.reset()
    editing.updateQuery(shortcut, fetchedQuery)
  }

  const reposFilterProps: ReposFilterProps = {
    options: {
      withOwnerTerms: true,
      withFinderTerms: true,
      withRelevanceSort: true,
      additionalFilterProviders,
    },
    id: 'repos-finder-filter',
    label: 'Search repositories',
    variant: 'input',
    placeholder: 'Search repositories',
    filterValue: query,
    orgs: payload.suggestedOrgs,
    definitions,
    trailingVisual,
    onChange: updateQuery,
    onSubmit: filterQuery => fetchResults({q: filterQuery.raw, page: 1, sidebarItemId: selectedSidebarItem?.id || ''}),
  }

  return {
    shortcuts,
    selectedSidebarItem,
    editing,
    deleting,
    methods: {
      selectSidebarLink,
      createShortcut,
      editShortcut,
      updateShortcut,
      fetchResults,
    },
    reposFilterProps,
    initialExpanded: payload.sidebarExpanded,
  }
}

function CustomPropertiesVisualIndicator({payload}: {payload?: PagePayload}) {
  const lines: string[] = []

  if (hasCustomProps(payload)) {
    const {source, definitions} = payload.customProperties
    const sourceLabel = source.type === 'business' ? 'enterprise' : 'organization'

    const namespaces = getPropertyNamespaces(definitions)

    if (namespaces.includes('props')) {
      lines.push(`Custom properties available for ${source.login} ${sourceLabel}`)
    }

    const externalPropertyNamespaces = namespaces.filter(prefix => prefix !== 'props')
    if (externalPropertyNamespaces.length > 0) {
      lines.push(
        `External custom properties available for ${source.login} ${sourceLabel} from ${formatListWithAnd(
          externalPropertyNamespaces,
        )}`,
      )
    }
  }

  if (hasDeploymentProps(payload)) {
    const providerNames = payload.additionalFilterProviders.join(', ')
    lines.push(`System properties available: ${providerNames}`)
  }

  return (
    <Tooltip text={lines.join('. ')} type="label">
      <Link href={customPropertiesDocsUrl}>
        <NoteIcon />
      </Link>
    </Tooltip>
  )
}

function hasCustomProps(
  payload: PagePayload | undefined,
): payload is PagePayload & {customProperties: NonNullable<PagePayload['customProperties']>} {
  return (payload?.customProperties?.definitions?.length ?? 0) > 0
}

function hasDeploymentProps(
  payload: PagePayload | undefined,
): payload is PagePayload & {additionalFilterProviders: NonNullable<PagePayload['additionalFilterProviders']>} {
  return (payload?.additionalFilterProviders?.length ?? 0) > 0
}

function formatListWithAnd(values: string[]): string {
  if (values.length <= 1) return values.join('')
  if (values.length === 2) return `${values[0]} and ${values[1]}`

  return `${values.slice(0, -1).join(', ')}, and ${values[values.length - 1]}`
}

function getPropertyNamespaces(definitions: PropertyDefinition[]) {
  return definitions.reduce<string[]>((namespaces, definition) => {
    const [prefix, propertyName] = definition.propertyName.split('.')
    const namespace = propertyName && prefix ? prefix : 'props'

    if (!namespaces.includes(namespace)) namespaces.push(namespace)

    return namespaces
  }, [] as string[])
}
